import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useState, useEffect, useCallback, useMemo } from "react";
import { AddressAutofill } from "@mapbox/search-js-react";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import {
  InputDS,
  LoaderOverlay,
  Spacer,
  TextCapitalized,
  isTripOngoing,
} from "@qivia/ui";
import { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import "../../../i18n/i18n";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import { LocationOnFilled } from "@qivia/ui";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { MaterialUiSingleDatePicker } from "@qivia/ui";
import { VehicleGeolocated } from "./index";
import { getDistanceInKm } from "./lib";
import { Trips, TripsDetails } from "../fleet/trips/tripsApi";
import { GeolocationSidePanel } from "./sidePanel";
import { VehiclesShowOnMap } from "./vehiclesShowOnMap";
import { VehicleTripOnMap } from "./vehiclesTripOnMap";

const accessToken = import.meta.env["VITE_MAPBOX_TOKEN"] as string;

const RecenterMap = ({ center }: { center: [number, number] }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, 14);
  }, [center, map]);
  return null;
};

interface SharedInterfaceGeolocationProps {
  lastDataPointsVehiclesToDisplayed: VehicleGeolocated[];
  fakeVehicleGeolocatedForDemo: VehicleGeolocated[];
  isNotProductionEnvironment: () => boolean;
  date: Date;
  onDateChange: (date: Date) => void;
  tripsByDayList: Trips[];
  fakeTripsByDayListForDemo: Trips[];
  isDataPointsLoading: boolean;
}

export const SharedInterfaceGeolocation = (
  props: SharedInterfaceGeolocationProps,
) => {
  const {
    lastDataPointsVehiclesToDisplayed,
    fakeVehicleGeolocatedForDemo,
    isNotProductionEnvironment,
    date,
    onDateChange,
    tripsByDayList,
    fakeTripsByDayListForDemo,
    isDataPointsLoading,
  } = props;

  const { t } = useTranslation();
  const [center, setCenter] = useState<[number, number]>([48.8566, 2.3522]);
  const [recenterKey, setRecenterKey] = useState(0);
  const [addressCoordinates, setAddressCoordinates] = useState<
    [number, number] | null
  >(null);
  const [addressDisplayed, setAddressDisplayed] = useState("");
  const [vehiclesToDisplay, setVehiclesToDisplay] = useState<
    VehicleGeolocated[]
  >([]);
  const [hoveredVehicleId, setHoveredVehicleId] = useState<string | null>(null);
  const [showTripForVehicleUuid, setShowTripForVehicleUuid] = useState<
    string | null
  >(null);

  useEffect(() => {
    const vehiclesEligibleForGeolocationDisplayed = isNotProductionEnvironment()
      ? [...fakeVehicleGeolocatedForDemo, ...lastDataPointsVehiclesToDisplayed]
      : lastDataPointsVehiclesToDisplayed;

    const withDistance = vehiclesEligibleForGeolocationDisplayed.map(
      (vehicle: VehicleGeolocated) => ({
        ...vehicle,
        distance: addressCoordinates
          ? getDistanceInKm(addressCoordinates, [
              vehicle.lastLatitude,
              vehicle.lastLongitude,
            ])
          : 0,
      }),
    );
    setVehiclesToDisplay(withDistance.sort((a, b) => a.distance - b.distance));
  }, [
    addressCoordinates,
    fakeVehicleGeolocatedForDemo,
    isNotProductionEnvironment,
    lastDataPointsVehiclesToDisplayed,
  ]);

  const allTripsByDayList = useMemo(
    () =>
      isNotProductionEnvironment()
        ? [...fakeTripsByDayListForDemo, ...tripsByDayList]
        : tripsByDayList,
    [fakeTripsByDayListForDemo, isNotProductionEnvironment, tripsByDayList],
  );

  const tripsDetailsForSelectedVehicle = useMemo<TripsDetails[]>(
    () =>
      allTripsByDayList
        .filter((t) => t.vehicleUuid === showTripForVehicleUuid)
        .flatMap((t) => t.trips),
    [allTripsByDayList, showTripForVehicleUuid],
  );

  const isSelectedVehicleTripOngoing = useMemo(() => {
    if (!showTripForVehicleUuid) return false;
    const vehicle = vehiclesToDisplay.find(
      (v) => v.uuid === showTripForVehicleUuid,
    );
    if (!vehicle) return false;
    if (!vehicle.dataPoints) return false;
    const lastDataPoint = vehicle.dataPoints[vehicle.dataPoints.length - 1];
    return isTripOngoing(lastDataPoint?.date, tripsDetailsForSelectedVehicle);
  }, [
    showTripForVehicleUuid,
    vehiclesToDisplay,
    tripsDetailsForSelectedVehicle,
  ]);

  const handleAutofill = useCallback(
    (coordinates: [number, number]) => {
      const [lon, lat] = coordinates;
      setCenter([lat, lon]);
      setAddressCoordinates([lat, lon]);
    },
    [setCenter, setAddressCoordinates],
  );

  const handleSelectVehicle = useCallback(
    (vehicle: VehicleGeolocated) => {
      setCenter([vehicle.lastLatitude, vehicle.lastLongitude]);
      setRecenterKey((prev) => prev + 1);
    },
    [setCenter, setRecenterKey],
  );

  return (
    <StyledFrame>
      {isDataPointsLoading && (
        <StyledLoaderWrapper>
          <LoaderOverlay visible={true} />
        </StyledLoaderWrapper>
      )}
      <StyledSearchBar>
        {/* @ts-expect-error AddressAutofill is not typed correctly */}
        <AddressAutofill
          accessToken={accessToken}
          onRetrieve={(e) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const address = feature.properties?.place_name;
            if (address) {
              setAddressDisplayed(address);
            }
            const coordinates = feature.geometry?.coordinates;
            if (coordinates) {
              handleAutofill(coordinates as [number, number]);
            }
          }}
        >
          <InputDS
            name="address"
            placeholder={t("geolocation.input.addressPlaceholder")}
            type="SEARCH"
            width={15}
            value={addressDisplayed}
            onChange={(e) => setAddressDisplayed(e.target.value)}
          />
        </AddressAutofill>
        <Spacer x={5} />
        <StyledColumn>
          <Spacer y={0.25} />
          <MaterialUiSingleDatePicker
            onChange={onDateChange}
            value={date}
            maxDate={new Date()}
          />
        </StyledColumn>
      </StyledSearchBar>
      <GeolocationSidePanel
        vehiclesToDisplay={vehiclesToDisplay}
        setHoveredVehicleId={setHoveredVehicleId}
        handleSelectVehicle={handleSelectVehicle}
      />
      <LeafletStyleOverrides />
      <MapContainer
        center={center}
        zoom={6}
        zoomControl={false}
        style={{ height: "100%", width: "100%" }}
        minZoom={2}
        maxZoom={18}
      >
        <TileLayer
          url={`https://api.mapbox.com/styles/v1/mapbox/light-v10/tiles/{z}/{x}/{y}?access_token=${accessToken}`}
          attribution=""
          tileSize={512}
          zoomOffset={-1}
        />
        {addressCoordinates && (
          <Marker position={addressCoordinates} icon={locationOnIcon}>
            <Tooltip
              direction="top"
              offset={L.point(0, -35)}
              opacity={1}
              permanent={false}
              className="address-tooltip"
            >
              <StyledAddressTooltip>
                <Spacer y={0.75} />
                <StyledFlex>
                  <Spacer x={0.75} />
                  <TextCapitalized>
                    {t("geolocation.tooltip.selectedAddress")}
                  </TextCapitalized>
                  <Spacer x={0.75} />
                </StyledFlex>
                <Spacer y={0.75} />
              </StyledAddressTooltip>
            </Tooltip>
          </Marker>
        )}

        <VehiclesShowOnMap
          handleSelectVehicle={handleSelectVehicle}
          hoveredVehicleId={hoveredVehicleId}
          setShowTripForVehicleUuid={setShowTripForVehicleUuid}
          showTripForVehicleUuid={showTripForVehicleUuid}
          vehiclesToDisplay={vehiclesToDisplay}
          date={date}
        />

        <VehicleTripOnMap
          showTripForVehicleUuid={showTripForVehicleUuid}
          isNotProductionEnvironment={isNotProductionEnvironment}
          fakeVehicleGeolocatedForDemo={fakeVehicleGeolocatedForDemo}
          lastDataPointsVehiclesToDisplayed={lastDataPointsVehiclesToDisplayed}
          isOngoing={isSelectedVehicleTripOngoing}
          tripsDetailsForSelectedVehicle={tripsDetailsForSelectedVehicle}
        />

        <RecenterMap key={recenterKey} center={center} />
      </MapContainer>
      <LeafletStyleOverrides />
    </StyledFrame>
  );
};

const StyledLocationOnIcon = styled.div`
  svg {
    fill: ${colors["colors/accent/900"]};
    height: 2.5rem;
    width: 2.5rem;
  }
`;

const locationOnIcon = L.divIcon({
  className: "custom-location-on-icon",
  html: ReactDOMServer.renderToString(
    <StyledLocationOnIcon>
      <LocationOnFilled />
    </StyledLocationOnIcon>,
  ),
  iconAnchor: [20, 35],
});

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 2rem;
`;

const StyledLoaderWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1200;
`;

const StyledFrame = styled.div`
  display: flex;
  position: relative;
  height: 100%;
  width: 100%;
  background-color: ${colors["colors/surfaces/background/background_level0"]};
  border-radius: 0.75rem 0 0 0.75rem;
  overflow: hidden;
`;

const StyledSearchBar = styled.div`
  display: flex;
  position: absolute;
  top: 1.5rem;
  left: 1rem;
  border: none;
  background: none;
  z-index: 1000;
`;

const LeafletStyleOverrides = createGlobalStyle`
  .vehicle-tooltip {
    background-color: transparent;
    border: 0px;
    box-shadow: none;
}

  .address-tooltip {
    background-color: ${colors["colors/surfaces/background/background_level2,5"]};
    border: 0px;
    box-shadow: none;
}
`;

const StyledAddressTooltip = styled.div`
  display: flex;
  flex-direction: column;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/black"]};
  border: 1px solid ${colors["colors/surfaces/background/background_level2,5"]};
  border-radius: 0.5rem;
  box-shadow: 0px 4px 16px 0px rgba(51, 51, 51, 0.15);
`;

const StyledFlex = styled.div`
  display: flex;
`;
